import { useState, useContext } from 'react';

import T from 'prop-types'
import { devConsoleLog } from '../../api/util'
import { getCurrentUserId } from '../../api/auth-service'
import OfficeContext from './OfficeContext';
import dayjs from 'dayjs'
import { useSeatMapBookings, useUserBookings } from '../../common/hooks'
import SeatMapContainer from './SeatMapContainer'
import BookListStatus from './BookListStatus'
import ScaleToFit from '../../common/components/ScaleToFit'
import Legend from './Legend'
import { Calendar } from '@/common/components/ui/calendar';
import {
	Card,
	CardContent,
	CardHeader,
	CardTitle
} from '@/common/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/common/components/ui/tabs"
import { Building } from 'lucide-react'

const OfficeContainer = ({ data: { offices } }) => {
	const { officeName, setOfficeName } = useContext(OfficeContext)
	const [date, setDate] = useState(dayjs().format('YYYY-MM-DD'));
	const userId = getCurrentUserId()
	const [bookings, requestBookings] = useSeatMapBookings(userId)
	const [userBookings, requestUserBookings] = useUserBookings(userId)

	const handleDayClick = (date) => {
		if (!date) return
		const isoDate = dayjs(date).format('YYYY-MM-DD')
		devConsoleLog('handleDayClick', isoDate)
		setDate(isoDate)
		renderBookings(isoDate, officeName)
	}

	const handleOfficeChange = (value) => {
		setOfficeName(value)
		renderBookings(date, value)
	}

	function renderBookings(selectedDate, selectedOffice) {
		requestBookings(selectedDate, selectedOffice)
	}

	function renderUserBookings(selectedOffice) {
		if (bookings.status.isResolved) {
			if (userBookings.status.isIdle) {
				requestUserBookings(selectedOffice) // fetch currentUserBookings
			} else {
				return <BookListStatus data={userBookings} onRefresh={() => requestUserBookings(selectedOffice)} />
			}
		}
		return <BookListStatus data={userBookings} onRefresh={() => requestUserBookings(selectedOffice)} />
	}

	return (
		<div className="grid grid-cols-1 lg:grid-cols-4 mt-[105px] pt-4 px-4 lg:px-5 gap-10 justify-items-center">
			<div className="col-span-1 flex flex-col gap-4 w-full lg:max-w-[360px] lg:min-w-[200px]">
				<Card className="w-full rounded-2xl border border-border/60 py-2 shadow-sm">
					<CardHeader>
						<CardTitle className="text-xl">Select a Date</CardTitle>
					</CardHeader>
					<CardContent className="grid gap-4 pb-5">
						<div className="w-full">
							<div className='grid gap-2 w-full'>
								<ScaleToFit>
									<Calendar
										mode="single"
										disabled={bookings.status.isPending}
										selected={date ? dayjs(date).toDate() : undefined}
										onSelect={handleDayClick}
										className="rounded-xl border border-border/60 bg-card"
									/>
								</ScaleToFit>
								<p className="text-sm text-muted-foreground">
									{date ? `You picked ${dayjs(date).format('DD MMM YYYY')}` : 'Please pick a day.'}
								</p>
							</div>
						</div>

					</CardContent>
				</Card>
				<Card className="hidden lg:flex flex-col w-full rounded-2xl border border-border/60 py-2 shadow-sm">
					<CardHeader>
						<CardTitle className="text-xl">Legend</CardTitle>
					</CardHeader>
					<CardContent className="pb-5">
						<Legend />
					</CardContent>
				</Card>
				{/* <Button onClick={logout}>Logout</Button> */}
			</div>
			<div className="col-span-1 lg:col-span-2 w-full">
				<Tabs value={officeName || offices[0]?.name} onValueChange={handleOfficeChange}>
					<TabsList className="py-5 w-full bg-white px-0">
						{offices.map((office) => (
							<TabsTrigger key={office.id} value={office.name} className="p-5">
								<Building />{office.location}
							</TabsTrigger>
						))}
					</TabsList>
					{offices.map((office) => (
						<TabsContent key={office.id} value={office.name} className='mt-4 w-full'>
							<Card className="rounded-2xl border border-border/60 shadow-sm gap-0">
								<CardHeader>
									<CardTitle className="text-xl">{office.name} Office</CardTitle>
								</CardHeader>
								<CardContent className='flex justify-center'>
									<SeatMapContainer data={bookings} onSuccessOpr={requestUserBookings} selectedOffice={office.name} officeName={office.name} />
								</CardContent>
							</Card>
						</TabsContent>
					))}
				</Tabs>
			</div>
			<div className="col-span-1 w-full lg:max-w-[360px] lg:min-w-[200px]">
				{renderUserBookings(officeName)}
			</div>
		</div>
	);
}

OfficeContainer.propTypes = {
	data: T.shape({
		offices: T.array.isRequired,
		status: T.object.isRequired,
		error: T.object,
	})
}
export default OfficeContainer;
